import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import ApiService from '../_services/ApiService';
import {
  createSubstitution,
  CreateSubstitutionInput,
  Substitution,
} from '../_services/SubstitutionService';
import { searchInstructors, Instructor } from '../_services/schedules.service';

interface TopicOption {
  id: number;
  name: string;
  courseName?: string | null;
  displayName: string;
}

interface SubstituteModalProps {
  show: boolean;
  activitySlotTemplateId: number;
  templateName?: string;
  templateStartTime?: string;
  templateEndTime?: string;
  originalInstructorId?: number;
  originalInstructorName?: string;
  defaultDate?: string;
  // 'request' goes into staff approval queue; 'approve' creates as APPROVED (last-minute path)
  mode?: 'request' | 'approve';
  onClose: () => void;
  onSaved?: (substitution: Substitution | null) => void;
}

const today = (): string => new Date().toISOString().slice(0, 10);

const SubstituteModal: React.FC<SubstituteModalProps> = ({
  show,
  activitySlotTemplateId,
  templateName,
  templateStartTime,
  templateEndTime,
  originalInstructorId,
  originalInstructorName,
  defaultDate,
  mode = 'approve',
  onClose,
  onSaved,
}) => {
  const [startDate, setStartDate] = useState<string>(defaultDate || today());
  const [endDate, setEndDate] = useState<string>(defaultDate || today());
  const [reason, setReason] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [results, setResults] = useState<Instructor[]>([]);
  const [searching, setSearching] = useState<boolean>(false);
  const [selected, setSelected] = useState<Instructor | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [topicSearch, setTopicSearch] = useState<string>('');
  const [topicResults, setTopicResults] = useState<TopicOption[]>([]);
  const [topicSearching, setTopicSearching] = useState<boolean>(false);
  const [selectedTopic, setSelectedTopic] = useState<TopicOption | null>(null);
  const [agenda, setAgenda] = useState<string>('');

  useEffect(() => {
    if (show) {
      setStartDate(defaultDate || today());
      setEndDate(defaultDate || today());
      setReason('');
      setSearch('');
      setResults([]);
      setSelected(null);
      setError(null);
      setTopicSearch('');
      setTopicResults([]);
      setSelectedTopic(null);
      setAgenda('');
    }
  }, [show, defaultDate]);

  // Topic search (debounced) — uses the global topics search endpoint
  useEffect(() => {
    if (!show || topicSearch.trim().length < 2) {
      setTopicResults([]);
      return;
    }
    let cancelled = false;
    setTopicSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res: any = await ApiService.searchTopics(topicSearch.trim());
        if (cancelled) return;
        const list = (res?.data?.data ?? []) as TopicOption[];
        setTopicResults(list);
      } catch {
        if (!cancelled) setTopicResults([]);
      } finally {
        if (!cancelled) setTopicSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [topicSearch, show]);

  useEffect(() => {
    if (!show || search.trim().length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await searchInstructors(search.trim());
        if (cancelled) return;
        const data = (res?.data ?? []).filter(
          (i) => !originalInstructorId || i.id !== originalInstructorId,
        );
        setResults(data);
      } catch (e) {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, show, originalInstructorId]);

  const dateRangeLabel = useMemo(() => {
    if (startDate === endDate) return `On ${startDate}`;
    return `${startDate} → ${endDate}`;
  }, [startDate, endDate]);

  const isApproveMode = mode === 'approve';
  const submitLabel = isApproveMode
    ? 'Assign Substitute'
    : 'Submit Request';

  const handleSubmit = async () => {
    setError(null);
    if (!startDate || !endDate) {
      setError('Start date and end date are required');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be on or before end date');
      return;
    }
    if (isApproveMode && !selected) {
      setError('Pick a substitute instructor before assigning');
      return;
    }

    const payload: CreateSubstitutionInput = {
      activitySlotTemplateId,
      startDate,
      endDate,
      reason: reason.trim() || undefined,
      status: isApproveMode ? 'APPROVED' : 'REQUESTED',
    };
    if (selected) {
      if (isApproveMode) {
        payload.substituteInstructorId = selected.id;
      } else {
        payload.suggestedInstructorId = selected.id;
      }
    }
    if (selectedTopic) {
      payload.substituteTopicId = selectedTopic.id;
    }
    if (agenda.trim()) {
      payload.substituteAgenda = agenda.trim();
    }

    try {
      setSubmitting(true);
      const created = await createSubstitution(payload);
      if (onSaved) onSaved(created);
      onClose();
    } catch (e: any) {
      const data = e?.response?.data;
      const detail = data?.error ? ` — ${data.error}` : '';
      setError(
        (data?.message || e?.message || 'Failed to save substitution') + detail,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isApproveMode ? 'Assign Substitute' : 'Request Substitute'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {(templateName || templateStartTime) && (
          <div
            style={{
              padding: '8px 12px',
              background: 'var(--surface-page)',
              border: '1px solid var(--border-default)',
              borderRadius: 4,
              marginBottom: 16,
              fontSize: 14,
            }}
          >
            {templateName && (
              <div>
                <strong>Slot:</strong> {templateName}
              </div>
            )}
            {templateStartTime && (
              <div>
                <strong>Time:</strong> {templateStartTime}
                {templateEndTime ? ` – ${templateEndTime}` : ''}
              </div>
            )}
            {originalInstructorName && (
              <div>
                <strong>Original Instructor:</strong> {originalInstructorName}
              </div>
            )}
            <div>
              <strong>Applies:</strong> {dateRangeLabel}
            </div>
          </div>
        )}

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Reason (optional)</Form.Label>
            <Form.Control
              type="text"
              maxLength={500}
              placeholder="e.g. Sick leave, Personal emergency"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>
              {isApproveMode ? 'Substitute Instructor' : 'Suggested Substitute (optional)'}
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Search by name or code (min 2 chars)"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (selected) setSelected(null);
              }}
            />
          </Form.Group>

          {selected && (
            <div
              style={{
                padding: '8px 12px',
                marginBottom: 12,
                background: '#e8f5e9',
                border: '1px solid #c8e6c9',
                borderRadius: 4,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>
                <strong>Selected:</strong> {selected.name}
                {selected.code ? ` (${selected.code})` : ''}
              </span>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setSelected(null)}
              >
                Change
              </Button>
            </div>
          )}

          {!selected && (searching || results.length > 0) && (
            <div
              style={{
                maxHeight: 220,
                overflowY: 'auto',
                border: '1px solid var(--border-default)',
                borderRadius: 4,
              }}
            >
              {searching && (
                <div style={{ padding: 12, textAlign: 'center' }}>
                  <Spinner animation="border" size="sm" /> Searching…
                </div>
              )}
              {!searching &&
                results.map((i) => (
                  <button
                    key={i.id}
                    type="button"
                    onClick={() => setSelected(i)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 12px',
                      border: 'none',
                      borderBottom: '1px solid #f1f3f5',
                      background: 'white',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>{i.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {i.code ? `${i.code} · ` : ''}
                      {i.email || ''}
                    </div>
                  </button>
                ))}
              {!searching && results.length === 0 && search.trim().length >= 2 && (
                <div style={{ padding: 12, color: 'var(--text-muted)' }}>
                  No instructors match.
                </div>
              )}
            </div>
          )}

          <hr style={{ margin: '20px 0 12px' }} />
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
            Optional: change topic or describe what the substitute will cover.
            Leave blank to inherit from the original plan.
          </div>

          <Form.Group className="mb-2">
            <Form.Label>Topic Override (optional)</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search topics by name (min 2 chars) — same or different course"
              value={topicSearch}
              onChange={(e) => {
                setTopicSearch(e.target.value);
                if (selectedTopic) setSelectedTopic(null);
              }}
            />
          </Form.Group>

          {selectedTopic && (
            <div
              style={{
                padding: '8px 12px',
                marginBottom: 12,
                background: '#fff8e1',
                border: '1px solid #ffe082',
                borderRadius: 4,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>
                <strong>Topic:</strong> {selectedTopic.displayName}
              </span>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setSelectedTopic(null)}
              >
                Clear
              </Button>
            </div>
          )}

          {!selectedTopic && (topicSearching || topicResults.length > 0) && (
            <div
              style={{
                maxHeight: 180,
                overflowY: 'auto',
                border: '1px solid var(--border-default)',
                borderRadius: 4,
                marginBottom: 12,
              }}
            >
              {topicSearching && (
                <div style={{ padding: 12, textAlign: 'center' }}>
                  <Spinner animation="border" size="sm" /> Searching…
                </div>
              )}
              {!topicSearching &&
                topicResults.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedTopic(t)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 12px',
                      border: 'none',
                      borderBottom: '1px solid #f1f3f5',
                      background: 'white',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>{t.name}</div>
                    {t.courseName && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {t.courseName}
                      </div>
                    )}
                  </button>
                ))}
              {!topicSearching &&
                topicResults.length === 0 &&
                topicSearch.trim().length >= 2 && (
                  <div style={{ padding: 12, color: 'var(--text-muted)' }}>
                    No topics match.
                  </div>
                )}
            </div>
          )}

          <Form.Group>
            <Form.Label>Agenda / Notes (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              maxLength={500}
              placeholder="e.g. Revision of last week's chapter, Supervised study, Watch documentary X"
              value={agenda}
              onChange={(e) => setAgenda(e.target.value)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" /> Saving…
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SubstituteModal;
